import { Link } from 'react-router-dom';
import { Dumbbell, Lock, CreditCard, TrendingUp, Shield, Zap, LayoutDashboard, Star, Quote, Phone, Instagram } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import api from '@/lib/api';

const LandingPage = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    setIsLoggedIn(!!token);
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    try {
      const response = await api.get('/testimonials');
      setTestimonials(response.data);
    } catch (error) {
      // Silently fail - testimonials are optional
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-slate-900 via-[#020617] to-black"></div>
        <div 
          className="absolute inset-0 opacity-40"
          style={{
            backgroundImage: `url('https://customer-assets.emergentagent.com/job_wholesome-health-1/artifacts/nkpcgc7y_IMG_2584.JPG')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        ></div>
        
        {/* Dashboard Icon - Top Right */}
        {isLoggedIn && (
          <div className="absolute top-4 right-4 sm:top-8 sm:right-8 z-20">
            <Link to="/dashboard">
              <Button
                size="sm"
                className="bg-primary/90 hover:bg-primary text-white shadow-lg hover:scale-105 transition-all sm:h-10 sm:px-4"
                data-testid="dashboard-icon-btn"
              >
                <LayoutDashboard className="w-4 h-4 sm:w-5 sm:h-5 sm:mr-2" />
                <span className="hidden sm:inline">Dashboard</span>
              </Button>
            </Link>
          </div>
        )}
        
        <div className="relative z-10 container mx-auto px-4 sm:px-6 md:px-12 lg:px-20 text-center">
          <div className="mb-6 sm:mb-8 flex justify-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_wholesome-health-1/artifacts/jxcg5bs9_WHOLESOME%20FITNESS.png" 
              alt="Wholesome Fitness Centre" 
              className="h-24 sm:h-32 md:h-40 w-auto object-contain"
              data-testid="gym-logo"
            />
          </div>
          <p className="text-base sm:text-xl md:text-2xl text-muted-foreground max-w-2xl mx-auto mb-6 sm:mb-8 px-2">
            State-of-the-art facility with smart access control. Your journey to wellness starts here.
          </p>
          <div className="flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4 justify-center px-4 sm:px-0">
            <Link to="/register" className="w-full sm:w-auto">
              <Button 
                size="lg" 
                className="w-full sm:w-auto bg-primary hover:bg-primary/90 text-white font-bold uppercase tracking-wider px-6 sm:px-8 py-5 sm:py-6 text-base sm:text-lg shadow-[0_0_20px_rgba(59,130,246,0.5)] hover:scale-105 transition-all"
                data-testid="hero-get-started-btn"
              >
                Get Started
              </Button>
            </Link>
            <Link to="/plans" className="w-full sm:w-auto">
              <Button 
                size="lg" 
                variant="outline"
                className="w-full sm:w-auto border-2 border-secondary text-secondary hover:bg-secondary hover:text-black font-bold uppercase tracking-wider px-6 sm:px-8 py-5 sm:py-6 text-base sm:text-lg transition-all"
                data-testid="hero-view-plans-btn"
              >
                View Plans
              </Button>
            </Link>
            <Link to="/store" className="w-full sm:w-auto">
              <Button 
                size="lg" 
                variant="outline"
                className="w-full sm:w-auto border-2 border-primary text-primary hover:bg-primary hover:text-white font-bold uppercase tracking-wider px-6 sm:px-8 py-5 sm:py-6 text-base sm:text-lg transition-all"
                data-testid="hero-store-btn"
              >
                Visit Store
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-12 sm:py-20 md:py-32 px-4 sm:px-6 md:px-12 lg:px-20">
        <div className="container mx-auto">
          <h2 className="text-2xl sm:text-4xl md:text-5xl font-heading font-black uppercase text-center mb-8 sm:mb-16 tracking-tight" data-testid="features-heading">
            Why Choose Us
          </h2>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-12 gap-4 sm:gap-6 md:gap-8">
            <div className="sm:col-span-1 md:col-span-6 bg-card/50 backdrop-blur-xl border border-white/10 rounded-xl p-6 sm:p-8 hover:border-primary/50 transition-colors" data-testid="feature-smart-access">
              <Lock className="w-10 h-10 sm:w-12 sm:h-12 text-primary mb-3 sm:mb-4" />
              <h3 className="text-xl sm:text-2xl font-heading font-bold uppercase mb-2 sm:mb-3">Smart Access Control</h3>
              <p className="text-sm sm:text-base text-muted-foreground">Enter with QR codes, OTP, or temporary passwords. Secure, seamless gym access anytime.</p>
            </div>
            
            <div className="sm:col-span-1 md:col-span-6 bg-card/50 backdrop-blur-xl border border-white/10 rounded-xl p-6 sm:p-8 hover:border-primary/50 transition-colors" data-testid="feature-flexible-payments">
              <CreditCard className="w-10 h-10 sm:w-12 sm:h-12 text-secondary mb-3 sm:mb-4" />
              <h3 className="text-xl sm:text-2xl font-heading font-bold uppercase mb-2 sm:mb-3">Flexible Payments</h3>
              <p className="text-sm sm:text-base text-muted-foreground">Multiple payment options including card payments and bank transfers. Pay securely and manage everything online.</p>
            </div>
            
            <div className="sm:col-span-1 md:col-span-4 bg-card/50 backdrop-blur-xl border border-white/10 rounded-xl p-6 sm:p-8 hover:border-primary/50 transition-colors" data-testid="feature-track-progress">
              <TrendingUp className="w-10 h-10 sm:w-12 sm:h-12 text-primary mb-3 sm:mb-4" />
              <h3 className="text-xl sm:text-2xl font-heading font-bold uppercase mb-2 sm:mb-3">Track Progress</h3>
              <p className="text-sm sm:text-base text-muted-foreground">Monitor your gym visits and subscription status in real-time.</p>
            </div>
            
            <div className="sm:col-span-1 md:col-span-4 bg-card/50 backdrop-blur-xl border border-white/10 rounded-xl p-6 sm:p-8 hover:border-primary/50 transition-colors" data-testid="feature-secure">
              <Shield className="w-10 h-10 sm:w-12 sm:h-12 text-secondary mb-3 sm:mb-4" />
              <h3 className="text-xl sm:text-2xl font-heading font-bold uppercase mb-2 sm:mb-3">100% Secure</h3>
              <p className="text-sm sm:text-base text-muted-foreground">Your data and payments protected with enterprise-grade security.</p>
            </div>
            
            <div className="sm:col-span-2 md:col-span-4 bg-card/50 backdrop-blur-xl border border-white/10 rounded-xl p-6 sm:p-8 hover:border-primary/50 transition-colors" data-testid="feature-instant-access">
              <Zap className="w-10 h-10 sm:w-12 sm:h-12 text-primary mb-3 sm:mb-4" />
              <h3 className="text-xl sm:text-2xl font-heading font-bold uppercase mb-2 sm:mb-3">Instant Access</h3>
              <p className="text-sm sm:text-base text-muted-foreground">Get immediate gym entry once your subscription is active.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Operating Hours Section */}
      <section className="py-16 px-6 md:px-12 lg:px-20 bg-gradient-to-r from-primary/10 via-transparent to-secondary/10">
        <div className="container mx-auto">
          <h2 className="text-4xl md:text-5xl font-heading font-black uppercase text-center mb-12 tracking-tight" data-testid="hours-heading">
            Operating Hours
          </h2>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6 max-w-5xl mx-auto">
            <div className="bg-card/50 backdrop-blur-xl border border-white/10 rounded-xl p-6 text-center hover:border-primary/50 transition-colors">
              <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Zap className="w-6 h-6 text-primary" />
              </div>
              <h3 className="font-heading font-bold uppercase text-lg mb-2">Morning Session</h3>
              <p className="text-muted-foreground text-sm mb-2">Monday - Friday</p>
              <p className="text-2xl font-bold text-primary">7AM - 12PM</p>
            </div>
            
            <div className="bg-card/50 backdrop-blur-xl border border-white/10 rounded-xl p-6 text-center hover:border-secondary/50 transition-colors">
              <div className="w-12 h-12 bg-secondary/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Zap className="w-6 h-6 text-secondary" />
              </div>
              <h3 className="font-heading font-bold uppercase text-lg mb-2">Evening Session</h3>
              <p className="text-muted-foreground text-sm mb-2">Monday - Friday</p>
              <p className="text-2xl font-bold text-secondary">6PM - 9PM</p>
            </div>
            
            <div className="bg-card/50 backdrop-blur-xl border border-white/10 rounded-xl p-6 text-center hover:border-primary/50 transition-colors">
              <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Dumbbell className="w-6 h-6 text-primary" />
              </div>
              <h3 className="font-heading font-bold uppercase text-lg mb-2">Saturday Morning</h3>
              <p className="text-muted-foreground text-sm mb-2">Morning Session</p>
              <p className="text-2xl font-bold text-primary">7AM - 12PM</p>
            </div>
            
            <div className="bg-card/50 backdrop-blur-xl border border-white/10 rounded-xl p-6 text-center hover:border-secondary/50 transition-colors">
              <div className="w-12 h-12 bg-secondary/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Dumbbell className="w-6 h-6 text-secondary" />
              </div>
              <h3 className="font-heading font-bold uppercase text-lg mb-2">Saturday Evening</h3>
              <p className="text-muted-foreground text-sm mb-2">Evening Session</p>
              <p className="text-2xl font-bold text-secondary">6PM - 7PM</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 md:px-12 lg:px-20 bg-gradient-to-b from-transparent via-primary/5 to-transparent">
        <div className="container mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-heading font-black uppercase mb-6 tracking-tight" data-testid="cta-heading">
            Ready to Transform?
          </h2>
          <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
            Join hundreds of members achieving their fitness goals at Wholesome Fitness Center.
          </p>
          <Link to="/register">
            <Button 
              size="lg"
              className="bg-secondary hover:bg-secondary/90 text-black font-bold uppercase tracking-wider px-10 py-6 text-lg"
              data-testid="cta-join-now-btn"
            >
              Join Now
            </Button>
          </Link>
        </div>
      </section>

      {/* Testimonials Section */}
      {testimonials.length > 0 && (
        <section className="py-20 px-6 md:px-12 lg:px-20">
          <div className="container mx-auto">
            <h2 className="text-4xl md:text-5xl font-heading font-black uppercase text-center mb-16 tracking-tight" data-testid="testimonials-heading">
              What Our Members Say
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {testimonials.slice(0, 3).map((testimonial) => (
                <Card key={testimonial.id} className="bg-card/50 backdrop-blur-xl border border-white/10" data-testid="testimonial-card">
                  <CardContent className="pt-6">
                    <Quote className="w-10 h-10 text-primary/50 mb-4" />
                    <p className="text-muted-foreground mb-6 italic">"{testimonial.content}"</p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {testimonial.user_image ? (
                          <img 
                            src={testimonial.user_image} 
                            alt={testimonial.user_name}
                            className="w-12 h-12 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                            <span className="text-primary font-bold text-lg">
                              {testimonial.user_name?.charAt(0) || 'M'}
                            </span>
                          </div>
                        )}
                        <div>
                          <p className="font-semibold">{testimonial.user_name}</p>
                          <p className="text-sm text-muted-foreground">Member</p>
                        </div>
                      </div>
                      <div className="flex gap-1">
                        {[...Array(testimonial.rating || 5)].map((_, i) => (
                          <Star key={i} className="w-4 h-4 fill-secondary text-secondary" />
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="py-12 px-6 md:px-12 lg:px-20 border-t border-white/10">
        <div className="container mx-auto max-w-6xl">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex justify-center md:justify-start mb-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_wholesome-health-1/artifacts/jxcg5bs9_WHOLESOME%20FITNESS.png" 
                  alt="Wholesome Fitness Centre" 
                  className="h-16 w-auto object-contain"
                />
              </div>
              <p className="text-sm text-muted-foreground">
                Your premier fitness destination with state-of-the-art facilities and smart technology.
              </p>
            </div>

            <div>
              <h4 className="font-heading font-bold uppercase mb-4">Quick Links</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><Link to="/about" className="hover:text-primary transition-colors">About Us</Link></li>
                <li><Link to="/plans" className="hover:text-primary transition-colors">Membership Plans</Link></li>
                <li><Link to="/store" className="hover:text-primary transition-colors">Gym Store</Link></li>
                <li><Link to="/faq" className="hover:text-primary transition-colors">FAQ</Link></li>
                <li><Link to="/contact" className="hover:text-primary transition-colors">Contact Us</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="font-heading font-bold uppercase mb-4">For Members</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><Link to="/login" className="hover:text-primary transition-colors">Login</Link></li>
                <li><Link to="/register" className="hover:text-primary transition-colors">Register</Link></li>
                <li><Link to="/dashboard" className="hover:text-primary transition-colors">Dashboard</Link></li>
                <li><Link to="/fitness" className="hover:text-primary transition-colors">Fitness Tracker</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="font-heading font-bold uppercase mb-4">Contact Info</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li>4 Owode Oyelaja, Tipper Garage, Ketu</li>
                <li>Lagos, Nigeria.</li>
                <li>+234 814 231 3787</li>
                <li>+234 803 346 9844</li>
                <li>wholesomefitnesscentre@gmail.com</li>
              </ul>
              <div className="flex gap-3 mt-4">
                <a 
                  href="https://wa.me/2348142313787" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="bg-green-500/20 p-2 rounded-lg hover:bg-green-500/30 transition-colors"
                  title="WhatsApp"
                >
                  <Phone className="w-4 h-4 text-green-500" />
                </a>
                <a 
                  href="https://instagram.com/wholesomefitness_center" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="bg-pink-500/20 p-2 rounded-lg hover:bg-pink-500/30 transition-colors"
                  title="Instagram: @wholesomefitness_center"
                >
                  <Instagram className="w-4 h-4 text-pink-500" />
                </a>
              </div>
            </div>
          </div>

          <div className="border-t border-white/10 pt-8 text-center text-sm text-muted-foreground">
            <p>&copy; 2026 Wholesome Fitness Center. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
